import { Suspense } from "react";
import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Rocket, ShieldAlert } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { useTracking } from "@/lib/tracker";
import { NewsTicker } from "@/components/NewsTicker";
import { SITE_NAME, AD_EMAIL, useRouteSeo, crumbsFor } from "@/lib/site";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const NAV = [
  { to: "/", label: "Home", id: "home" },
  { to: "/latest-news", label: "Latest News", id: "news" },
  { to: "/spacex-stock-price", label: "SPCX Stock", id: "stock" },
  { to: "/trump-accounts-guide", label: "Trump Accounts", id: "trump-accounts" },
  { to: "/custodial-accounts", label: "State Guides", id: "states" },
  { to: "/resources", label: "Resources", id: "resources" },
  { to: "/faqs", label: "FAQs", id: "faqs" },
  { to: "/advertise", label: "Advertise With Us", id: "advertise" },
];

const linkCls = ({ isActive }) =>
  `text-sm font-medium transition-colors ${isActive ? "text-white" : "text-slate-400 hover:text-white"}`;

const Logo = () => (
  <Link to="/" data-testid="site-logo" className="flex items-center gap-2.5 shrink-0">
    <span className="grid h-8 w-8 place-items-center rounded-md bg-[#0052FF]"><Rocket className="h-4 w-4 text-white" /></span>
    <span className="font-display font-extrabold tracking-tight text-white leading-none">
      SPACEX CHILD SHARES <span className="text-sky-400">NEWS</span>
    </span>
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-[#0B0E14]/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden xl:flex items-center gap-6">
          {NAV.map((n) => (
            <NavLink key={n.id} to={n.to} end data-testid={`nav-link-${n.id}`} className={linkCls}>{n.label}</NavLink>
          ))}
        </nav>
        <button data-testid="mobile-menu-toggle" aria-label="Menu" onClick={() => setOpen(!open)} className="xl:hidden p-2 text-slate-300">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <nav data-testid="mobile-menu" className="xl:hidden border-t border-slate-800 bg-[#0B0E14] px-4 sm:px-6 lg:px-8 py-3 flex flex-col gap-3">
          {NAV.map((n) => (
            <NavLink key={n.id} to={n.to} end onClick={() => setOpen(false)} data-testid={`mobile-nav-link-${n.id}`} className={linkCls}>{n.label}</NavLink>
          ))}
        </nav>
      )}
    </header>
  );
};

const DisclaimerBar = () => (
  <div data-testid="disclaimer-banner" className="border-b border-amber-500/20 bg-[#121722]">
    <p className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2 text-[11px] sm:text-xs text-amber-200/80 flex items-start gap-2">
      <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      Independent news and resource site. Not affiliated with SpaceX or Elon Musk. We do not distribute shares, process applications or collect children's information.
    </p>
  </div>
);

const Footer = () => (
  <footer className="mt-16 border-t border-slate-800 bg-[#080B10] pt-12 pb-8 text-sm text-slate-400">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-10 md:grid-cols-3">
      <div className="space-y-3">
        <Logo />
        <p className="text-xs leading-relaxed">An independent news aggregation website. Headlines and short summaries link to the original publishers. Nothing on this site is financial advice.</p>
      </div>
      <div className="flex flex-col gap-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Site</p>
        {NAV.map((n) => <Link key={n.id} to={n.to} data-testid={`footer-link-${n.id}`} className="hover:text-white transition-colors">{n.label}</Link>)}
        <Link to="/news" data-testid="footer-link-all-stories" className="hover:text-white transition-colors">All Stories</Link>
        <Link to="/topics/child-share-pledge" data-testid="footer-link-topic-pledge" className="hover:text-white transition-colors">Pledge News</Link>
        <Link to="/topics/spcx-stock" data-testid="footer-link-topic-stock" className="hover:text-white transition-colors">SPCX Stock News</Link>
        <Link to="/topics/trump-accounts" data-testid="footer-link-topic-trump" className="hover:text-white transition-colors">Trump Accounts News</Link>
        <Link to="/trump-accounts-calculator" data-testid="footer-link-calculator" className="hover:text-white transition-colors">Trump Account Calculator</Link>
      </div>
      <div className="flex flex-col gap-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Legal</p>
        <Link to="/about" data-testid="footer-link-about" className="hover:text-white">About / Disclaimer</Link>
        <Link to="/privacy-policy" data-testid="footer-link-privacy" className="hover:text-white">Privacy Policy</Link>
        <Link to="/advertising-disclosure" data-testid="footer-link-ad-disclosure" className="hover:text-white">Advertising Disclosure</Link>
        <a href={`mailto:${AD_EMAIL}`} data-testid="footer-email-link" className="hover:text-white">{AD_EMAIL}</a>
      </div>
    </div>
    <p className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-10 pt-6 border-t border-slate-800/70 text-xs text-slate-500">
      © {new Date().getFullYear()} {SITE_NAME}. Independent and not affiliated with, endorsed by, or connected to SpaceX or Elon Musk. SpaceX is a trademark of Space Exploration Technologies Corp.
    </p>
  </footer>
);

const RouteCrumbs = () => {
  const items = crumbsFor(useLocation().pathname.replace(/\/+$/, "") || "/");
  if (!items) return null;
  return <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-6"><Breadcrumbs items={items} /></div>;
};

const RouteSeo = () => {
  useTracking();
  useRouteSeo(useLocation().pathname.replace(/\/+$/, "") || "/");
  return null;
};

export const Layout = () => (
  <div className="min-h-screen flex flex-col">
    <RouteSeo />
    <Header />
    <NewsTicker />
    <DisclaimerBar />
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-5">
      <AdSlot id="top-banner" variant="leaderboard" />
    </div>
    <main className="flex-1"><RouteCrumbs /><Suspense fallback={<div className="min-h-[60vh]" />}><Outlet /></Suspense></main>
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 mt-12">
      <AdSlot id="bottom-banner" variant="leaderboard" />
    </div>
    <Footer />
  </div>
);
